import { ArrowLeft } from 'lucide-react'
import { useLinkClickHandler } from 'react-router'
import { Alert } from '../common/Alert'
import { Button, LinkButton } from '../common/Button'
import { StatePanel } from '../common/StatePanel'
import { StatusBadge } from '../common/StatusBadge'
import { AdvancedDisclosure } from '../layout'
import styles from '../CommandCenterApprovals.module.css'
import {
  POST_DELETING_MESSAGE,
  canDecide,
  canOverturn,
  carriedApprovalSentence,
  formatDecidedAt,
  isOverdue,
  notCurrentMessage,
  postExpiredMessage,
  previousApprovalSentence,
  reviewDisplayTitle,
  reviewStatusLabel,
  reviewStatusTone,
  snapshotFields,
  sourceLabel,
  supersededMessage,
} from './moderationReviewModel'
import {
  findingFieldLabel,
  findingLabel,
  findingSourceLabel,
  findingTimeOffset,
  formatTimeOffset,
  isSpokenFinding,
  isTranscriptField,
  snapshotTexts,
} from './moderationFindings'
import { HighlightedText } from './HighlightedText'
import { ReviewMedia } from './ReviewMedia'

const EXCERPT_CLASSES = { root: styles.transcriptExcerpt, label: styles.transcriptLabel, mark: styles.transcriptMark }

// excerptFields picks, for each finding, the transcript chunk to quote under
// it: any finding raised on speech whose chunk text the review carries. A
// phrase match marks its words; a classifier flag has no span, so its chunk
// is quoted plain for the reviewer to read at that time. Each chunk is
// quoted once, under its first finding, with every match in it marked.
function excerptFields(findings, texts) {
  const seen = new Set()
  return findings.map((finding) => {
    const field = finding?.field
    if (!isSpokenFinding(finding) || !isTranscriptField(field) || typeof texts?.[field] !== 'string') return null
    if (seen.has(field)) return null
    seen.add(field)
    return field
  })
}

function rawTime(finding) {
  const time = formatTimeOffset(findingTimeOffset(finding))
  return time ? ` · at ${time}` : ''
}

function Findings({ findings = [], texts }) {
  if (!findings.length) return <p>No findings were recorded.</p>
  const excerpts = excerptFields(findings, texts)
  return (
    <ul aria-label="Moderation findings">
      {findings.map((finding, index) => (
        <li key={`${finding.category}-${finding.source}-${index}`}>
          <StatusBadge status={finding.severity === 'block' ? 'error' : 'warning'} size="small">
            {finding.severity === 'block' ? 'Block' : 'Flag'}
          </StatusBadge>{' '}
          {findingLabel(finding, texts)}
          {excerpts[index] ? (
            <HighlightedText
              label={findingFieldLabel(excerpts[index])}
              field={excerpts[index]}
              text={texts[excerpts[index]]}
              findings={findings}
              classes={EXCERPT_CLASSES}
              plain
            />
          ) : null}
        </li>
      ))}
    </ul>
  )
}

// OpenPostLink opens the post's editor in the app.
function OpenPostLink({ postId }) {
  const href = `/dashboard/feed/posts/${encodeURIComponent(postId)}/edit`
  const onClick = useLinkClickHandler(href)
  return <LinkButton className={styles.openPost} href={href} variant="ghost" onClick={onClick}>Open post</LinkButton>
}

// ReviewDecisionPanel renders the immutable snapshot the review was opened
// for, never the live post, with the findings, source and author. notice is
// why the last decision on this review was refused ({ tone, message,
// reason }); onNext, when set, offers the next listed review, and
// onOpenReview(id) opens another review (a carried approval's original).
// onOverturn starts the overturn of a rejection. onRetry reads the review
// again: after it failed to load, or when a media link expired.
export function ReviewDecisionPanel({
  review,
  loading = false,
  error,
  notice,
  deciding = false,
  onRetry,
  onApprove,
  onReject,
  onOverturn,
  onNext,
  onOpenReview,
  onBack,
  startFocusRef,
}) {
  if (loading && !review) {
    return (
      <section className={styles.decisionPanel} aria-label="Moderation decision">
        <StatePanel compact state="loading" title="Loading the review" description="Decision controls stay unavailable until the review is ready." />
      </section>
    )
  }
  if (error) {
    return (
      <section className={styles.decisionPanel} aria-label="Moderation decision">
        <StatePanel compact state="error" title="The review could not load" description="Nothing can be decided until the review is available." onRetry={onRetry} />
      </section>
    )
  }
  if (!review) {
    return (
      <section className={styles.decisionPanel} aria-label="Moderation decision">
        <StatePanel compact state="empty" title="Select a review" description="Choose a held post to see what it said and why it was held." />
      </section>
    )
  }

  const fields = snapshotFields(review.snapshot)
  const texts = snapshotTexts(review.snapshot, review.transcripts)
  const decidable = canDecide(review)
  const overturned = review.overturned
  // A rejection of a revision the post has since moved on from: the new
  // version is reviewed on its own, so this one cannot be overturned.
  const rejectedThenEdited = review.status === 'rejected'
    && review.post_exists !== false
    && Boolean(review.current?.content_revision)
    && review.current.content_revision !== review.content_revision
  const autoRejected = review.status === 'auto_rejected'
  const open = review.status === 'pending' || autoRejected
  // A pending delete only matters to a review that could still be decided;
  // a history entry keeps reading as what was decided.
  const pendingDelete = open && review.post_exists !== false && review.post_pending_delete === true
  // Likewise an expired post: the review returns once the expiry moves later.
  const expired = open && review.post_exists !== false && !pendingDelete && review.post_expired === true
  // The deleting and expired warnings already say why nothing can be
  // decided; a refused decision with the same reason is not repeated under
  // them.
  const shownNotice = (pendingDelete && notice?.reason === 'post_deleting') || (expired && notice?.reason === 'post_expired')
    ? null
    : notice

  return (
    <section className={styles.decisionPanel} aria-labelledby="moderation-decision-title">
      <Button className={styles.mobileBack} variant="ghost" onClick={onBack}>
        <ArrowLeft size={17} aria-hidden="true" />
        Reviews
      </Button>

      <header className={styles.decisionHeader}>
        <div className={styles.decisionHeading}>
          <div className={styles.decisionTitleLine}>
            <h2 ref={startFocusRef} id="moderation-decision-title" tabIndex={-1}>{reviewDisplayTitle(review)}</h2>
            <span className={styles.badgeRow}>
              <StatusBadge status={reviewStatusTone(review.status)}>{reviewStatusLabel(review.status)}</StatusBadge>
              {isOverdue(review) ? <StatusBadge status="error">Overdue</StatusBadge> : null}
              {review.self_review ? <StatusBadge status="info">Self-review</StatusBadge> : null}
              {review.carried_from ? <StatusBadge status="info">Carried over</StatusBadge> : null}
              {overturned ? <StatusBadge status="info">Overturned</StatusBadge> : null}
            </span>
          </div>
          <span>
            {sourceLabel(review.source)}
            {' · '}
            {review.author_email ? `Written by ${review.author_email}` : 'Written by the system'}
          </span>
        </div>
        {review.post_exists !== false && review.post_id ? <OpenPostLink postId={review.post_id} /> : null}
      </header>

      {decidable && isOverdue(review) ? (
        <Alert variant="warning">
          {`This review has waited longer than this brand's review target. Brand admins were reminded on ${formatDecidedAt(review.escalated_at)}.`}
        </Alert>
      ) : null}
      {autoRejected && !pendingDelete && !expired ? (
        <Alert variant="info">
          This post was rejected automatically because this brand does not require review of flagged content. You can approve it to overturn the rejection.
        </Alert>
      ) : null}
      {review.status === 'superseded' && review.post_exists !== false ? <Alert variant="info">{supersededMessage(review)}</Alert> : null}
      {!review.post_exists ? (
        <Alert variant="warning">The post was deleted. This review is kept as history and can no longer be decided.</Alert>
      ) : pendingDelete ? (
        <Alert variant="warning">{POST_DELETING_MESSAGE}</Alert>
      ) : expired ? (
        <Alert variant="warning">{postExpiredMessage(review.post_expires_at)}</Alert>
      ) : review.is_current === false && open ? (
        <Alert variant="warning">{notCurrentMessage(review)}</Alert>
      ) : null}
      {open && review.previous_approval?.new_findings?.length ? (
        <Alert variant="info">
          <p>{previousApprovalSentence(review.previous_approval)}</p>
          <ul aria-label="New findings">
            {review.previous_approval.new_findings.map((finding, index) => (
              <li key={`${finding.category}-${index}`}>{findingLabel(finding, texts)}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <section className={styles.consequence} aria-labelledby="moderation-snapshot-title">
        <h3 id="moderation-snapshot-title">What the post said</h3>
        {fields.length ? (
          <dl>
            {fields.map((field) => (
              <div key={field.label}>
                <dt>{field.label}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
        ) : <p>The snapshot has no text.</p>}
        <ReviewMedia reviewId={review.id} items={review.media || []} onLinkExpired={onRetry} />
      </section>

      <section className={styles.consequence} aria-labelledby="moderation-findings-title">
        <h3 id="moderation-findings-title">Why it was held</h3>
        <Findings findings={review.findings} texts={texts} />
      </section>

      {review.decided_at ? (
        <section className={styles.consequence} aria-labelledby="moderation-decision-record-title">
          <h3 id="moderation-decision-record-title">Decision</h3>
          {review.carried_from ? (
            <>
              <p>{carriedApprovalSentence(review)}</p>
              {onOpenReview ? (
                <Button variant="ghost" onClick={() => onOpenReview(review.carried_from.review_id)}>View the original approval</Button>
              ) : null}
            </>
          ) : (
            <>
              {overturned ? (
                <>
                  <p>
                    Rejected by {overturned.by_email || 'a former admin'}
                    {formatDecidedAt(overturned.at) ? ` on ${formatDecidedAt(overturned.at)}` : ''}.
                  </p>
                  {overturned.reason ? <p>Reason: {overturned.reason}</p> : null}
                </>
              ) : null}
              <p>
                {overturned ? 'Overturned and approved' : reviewStatusLabel(review.status)} by {review.decided_by_email || 'a former admin'} on{' '}
                {new Date(review.decided_at).toLocaleString()}
                {review.self_review ? ' on their own post' : ''}.
              </p>
            </>
          )}
          {review.decision_reason ? <p>Reason: {review.decision_reason}</p> : null}
          {rejectedThenEdited ? <p>The post was edited after this rejection; its new version is reviewed on its own.</p> : null}
        </section>
      ) : null}

      <AdvancedDisclosure label="Technical details">
        <dl className={styles.technicalDetails}>
          <div><dt>Revision</dt><dd>{review.content_revision}</dd></div>
          <div><dt>Content hash</dt><dd><code>{review.content_hash}</code></dd></div>
          <div><dt>Policy</dt><dd><code>{review.policy_fingerprint}</code></dd></div>
          <div><dt>Request version</dt><dd>{review.request_version}</dd></div>
          {(review.findings || []).map((finding, index) => (
            <div key={`raw-${index}`}>
              <dt>{finding.category}</dt>
              <dd>
                {findingSourceLabel(finding.source)}
                {typeof finding.score === 'number' ? ` · score ${finding.score.toFixed(2)}` : ''}
                {rawTime(finding)}
              </dd>
            </div>
          ))}
        </dl>
      </AdvancedDisclosure>

      {shownNotice ? <Alert variant={shownNotice.tone === 'error' ? 'error' : 'warning'}>{shownNotice.message}</Alert> : null}
      {onNext ? (
        <div className={styles.decisionActions}>
          <Button variant="secondary" onClick={onNext}>Next review</Button>
        </div>
      ) : null}

      {canOverturn(review) ? (
        <div className={styles.decisionActions} aria-label="Moderation decision actions">
          <Button disabled={deciding} onClick={onOverturn}>Overturn and approve</Button>
        </div>
      ) : null}

      {decidable ? (
        <div className={styles.decisionActions} aria-label="Moderation decision actions">
          <Button variant="danger" disabled={deciding} onClick={onReject}>Reject</Button>
          <Button loading={deciding} onClick={onApprove}>{autoRejected ? 'Overturn and approve' : 'Approve'}</Button>
        </div>
      ) : null}
    </section>
  )
}
